"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, Lightbulb, Megaphone, Calendar, Palette, MonitorSmartphone, Users, ChartBar } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const solutions = [{
  id: "communication",
  icon: Megaphone,
  label: "Communication Stratégique",
  title: "Influence et Réputation",
  description: "Nous élaborons des stratégies de communication sur-mesure pour renforcer votre positionnement institutionnel et gérer votre réputation auprès des parties prenantes clés.",
  m1: "Audit de perception et analyse du contexte",
  m2: "Définition des messages clés et du ton",
  m3: "Planification stratégique multicanale",
  m4: "Mesure d'impact et ajustements continus",
  s1: "Relations Publiques & Presse",
  s2: "Communication de Crise",
  s3: "Communication Interne",
  s4: "Lobbying & Affaires Publiques",
  image: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&auto=format&fit=crop",
  cta: "Demander un audit"
}, {
  id: "events",
  icon: Calendar,
  label: "Événementiel Professionnel",
  title: "Expériences Mémorables",
  description: "De la conception à la réalisation, nous orchestrons des événements professionnels haut de gamme qui marquent les esprits et servent vos objectifs d'affaires.",
  m1: "Conception créative et scénographie",
  m2: "Logistique de précision et coordination",
  m3: "Gestion des invités et protocole",
  m4: "Supervision technique et artistique",
  s1: "Séminaires & Conventions",
  s2: "Lancements de Produits",
  s3: "Soirées de Gala & Awards",
  s4: "Team Building Stratégique",
  image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=2069&auto=format&fit=crop",
  cta: "Planifier un événement"
}, {
  id: "branding",
  icon: Palette,
  label: "Branding & Identité",
  title: "Identité de Marque Forte",
  description: "Nous forgeons des identités visuelles et narratives cohérentes qui incarnent vos valeurs et vous distinguent durablement dans un environnement concurrentiel.",
  m1: "Ateliers de plateforme de marque",
  m2: "Création de l'univers graphique",
  m3: "Déclinaison sur tous les supports",
  m4: "Charte éditoriale et ton de voix",
  s1: "Création de Logo & Charte",
  s2: "Rebranding Complet",
  s3: "Design de Supports Print",
  s4: "Packaging & Signalétique",
  image: "https://images.unsplash.com/photo-1558655146-d09347e92766?q=80&w=1964&auto=format&fit=crop",
  cta: "Révéler votre marque"
}, {
  id: "digital",
  icon: MonitorSmartphone,
  label: "Communication Digitale",
  title: "Performance Numérique",
  description: "Une approche ROIste du digital pour amplifier votre visibilité, engager vos audiences cibles et générer des opportunités qualifiées en B2B.",
  m1: "Audit de présence digitale",
  m2: "Stratégie de contenu (Inbound)",
  m3: "Optimisation des parcours utilisateurs",
  m4: "Reporting et analyse de données",
  s1: "Social Media Management B2B",
  s2: "Campagnes Publicitaires (Ads)",
  s3: "Création de Contenu Vidéo/Photo",
  s4: "Développement Web & SEO",
  image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070&auto=format&fit=crop",
  cta: "Booster votre visibilité"
}];
export default function B2BSolutionsCatalog() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="catalogue-de-solutions-b2b" className="dark relative py-32 bg-secondary text-foreground overflow-hidden">
      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-primary/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[700px] h-[700px] bg-primary/10 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div ref={ref} className={`max-w-4xl mx-auto text-center mb-20 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <span className="inline-block font-mono text-sm font-medium text-primary tracking-[0.3em] uppercase mb-6">
            NOS EXPERTISES STRATÉGIQUES
          </span>
          <h2 className="text-4xl md:text-6xl font-serif font-bold mb-8 leading-tight">
            Des solutions intégrées pour <br className="hidden md:block" />
            votre <span className="text-primary italic">croissance durable</span>
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 leading-relaxed max-w-3xl mx-auto font-sans">
            Une approche méthodique alliant créativité stratégique et excellence opérationnelle pour répondre aux exigences complexes des organisations B2B.
          </p>
        </div>

        <Tabs defaultValue="communication" className="w-full">
          <TabsList className="w-full flex flex-wrap justify-center gap-3 bg-transparent h-auto p-0 mb-20 border-none">
            {solutions.map((solution, i) => <TabsTrigger key={solution.id} value={solution.id} data-index={i} className="px-8 py-4 rounded-sm border border-white/10 bg-white/5 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:border-primary transition-all duration-300 text-white/60 hover:text-white hover:bg-white/10 h-auto">
                <solution.icon className="w-4 h-4 mr-3" />
                <span className="font-mono text-xs uppercase tracking-widest font-bold">{solution.label}</span>
              </TabsTrigger>)}
          </TabsList>

          {solutions.map((solution, i) => {
          const methodology = [solution.m1, solution.m2, solution.m3, solution.m4];
          const services = [solution.s1, solution.s2, solution.s3, solution.s4];
          return <TabsContent key={solution.id} value={solution.id} className="focus-visible:outline-none focus-visible:ring-0 mt-0 animate-in fade-in slide-in-from-bottom-8 duration-700">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                  {}
                  <div className="lg:col-span-7 space-y-10">
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 text-primary">
                        <div className="h-px w-8 bg-primary" />
                        <span className="font-mono text-xs uppercase tracking-widest font-bold">Expertise Focus</span>
                      </div>
                      <h3 className="text-4xl md:text-5xl font-serif font-bold leading-tight">
                        {solution.title}
                      </h3>
                      <p className="text-lg text-foreground/80 leading-relaxed font-sans border-l-2 border-primary/30 pl-6 italic">
                        {solution.description}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {}
                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-sm group hover:border-primary/50 transition-colors duration-300">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-2 bg-primary/10 rounded-sm text-primary">
                            <Target className="w-5 h-5" />
                          </div>
                          <h4 className="font-mono text-xs uppercase tracking-wider font-bold">Méthodologie</h4>
                        </div>
                        <ul className="space-y-4">
                          {methodology.map((item, idx) => <li key={idx} className="flex items-start gap-4 text-sm text-foreground/70 leading-snug">
                              <span className="mt-1.5 w-1 h-1 rounded-full bg-primary shrink-0" />
                              {item}
                            </li>)}
                        </ul>
                      </div>

                      {}
                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-sm group hover:border-primary/50 transition-colors duration-300">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-2 bg-primary/10 rounded-sm text-primary">
                            <Lightbulb className="w-5 h-5" />
                          </div>
                          <h4 className="font-mono text-xs uppercase tracking-wider font-bold">Services Clés</h4>
                        </div>
                        <ul className="space-y-4">
                          {services.map((item, idx) => <li key={idx} className="flex items-start gap-4 text-sm text-foreground/70 leading-snug">
                              <ArrowRight className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                              {item}
                            </li>)}
                        </ul>
                      </div>
                    </div>

                    <div className="pt-4">
                      <Button asChild size="lg" className="rounded-sm bg-primary hover:bg-primary/90 text-white font-semibold px-10 h-14 text-base transition-all hover:shadow-[0_0_25px_rgba(212,28,61,0.4)] hover:-translate-y-1">
                        <Link to="#">
                          {solution.cta}
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                  {}
                  <div className="lg:col-span-5 relative">
                    <div className="relative aspect-[4/5] rounded-sm overflow-hidden group">
                      <div className="absolute inset-0 bg-gradient-to-t from-secondary via-transparent to-transparent z-10 opacity-80" />
                      <img src={solution.image} data-wvc-srcset={`${solution.image}-wvc-srcset`} data-wvc-sizes={`${solution.image}-wvc-sizes`} alt={solution.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                      
                      {}
                      <div className="absolute bottom-8 left-8 right-8 z-20">
                        <div className="bg-white/10 backdrop-blur-2xl border border-white/20 p-6 rounded-sm flex items-center gap-5 shadow-2xl">
                          <div className="bg-primary p-4 rounded-sm text-white shadow-lg">
                            {solution.id === 'communication' && <Users className="w-6 h-6" />}
                            {solution.id === 'events' && <SafeHydrate><Calendar className="w-6 h-6" /></SafeHydrate>}
                            {solution.id === 'branding' && <Palette className="w-6 h-6" />}
                            {solution.id === 'digital' && <ChartBar className="w-6 h-6" />}
                          </div>
                          <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary font-bold mb-1">Division</p>
                            <p className="text-white font-serif text-lg font-bold">{solution.label}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {}
                    <div className="absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-primary/40 rounded-tr-sm pointer-events-none" />
                    <div className="absolute -bottom-4 -left-4 w-24 h-24 border-b-2 border-l-2 border-primary/40 rounded-bl-sm pointer-events-none" />
                  </div>
                </div>
              </TabsContent>;
        })}
        </Tabs>
      </div>
    </section>;
}